import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import { ContentConditionSourceEnum } from '@typing/Enums';
import {
  ArticleFragment as ArticleType,
  CareProfileActionableFieldEnum,
  CareProfileActionableFieldValueEnum,
  ClientRoleProviderEmotionalStateEnum,
  ClientRoleProviderFeelsSupportedEnum,
  ClientRoleRelationshipEnum,
  ConditionalContentTypeEnum,
  ContentConditionCreateDocument,
  ContentConditionCreateMutation,
  ContentConditionCreateMutationVariables,
  ContentConditionKindEnum,
  GoalTemplateFragment as GoalTemplateType,
  StageEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { contentConditionKindsForSources } from '@utils/modelUtils';
import {
  optionsForCareProfileActionableField,
  optionsForCareProfileActionableFieldValue,
  optionsForRelationship,
  optionsForStage
} from '@utils/optionUtils';

export type FormData = {
  careProfileField: CareProfileActionableFieldEnum | null;
  careProfileValue: CareProfileActionableFieldValueEnum | null;
  id?: string | null;
  kind: ContentConditionKindEnum | null;
  providerEmotionalState: ClientRoleProviderEmotionalStateEnum | null;
  providerFeelsSupported: ClientRoleProviderFeelsSupportedEnum | null;
  relationship: ClientRoleRelationshipEnum | null;
  source: ContentConditionSourceEnum | null;
  stage: StageEnum | null;
};

type Props = {
  article?: ArticleType;
  goalTemplate?: GoalTemplateType;
  onCancel: () => void;
  onSuccess: (data?: FormData) => void;
};

const ContentConditionForm = ({ article, goalTemplate, onCancel, onSuccess }: Props) => {
  const intl = useIntl();

  const defaultValues: FormData = {
    careProfileField: null,
    careProfileValue: null,
    kind: null,
    providerEmotionalState: null,
    providerFeelsSupported: null,
    relationship: null,
    source: null,
    stage: null
  };
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      careProfileField: yup
        .string()
        .defined()
        .oneOf(Object.values(CareProfileActionableFieldEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.care_profile_field_value) {
            return schema.required(intl.formatMessage({ id: 'errors.contentConditions.careProfileField.required' }));
          }
          return schema.nullable();
        }),
      careProfileValue: yup
        .string()
        .defined()
        .oneOf(Object.values(CareProfileActionableFieldValueEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.care_profile_field_value) {
            return schema.required(intl.formatMessage({ id: 'errors.contentConditions.careProfileValue.required' }));
          }
          return schema.nullable();
        }),
      id: yup.string().nullable(),
      kind: yup
        .string()
        .defined()
        .oneOf(Object.values(ContentConditionKindEnum))
        .when('source', ([source], schema) => {
          if (source !== ContentConditionSourceEnum.CARE_PROFILE && source !== ContentConditionSourceEnum.ALWAYS) {
            return schema.required(intl.formatMessage({ id: 'errors.contentConditions.kind.required' }));
          }
          return schema.nullable();
        }),

      providerEmotionalState: yup
        .string()
        .defined()
        .oneOf(Object.values(ClientRoleProviderEmotionalStateEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.provider_emotional_state_selected) {
            return schema.required(
              intl.formatMessage({ id: 'errors.contentConditions.providerEmotionalState.required' })
            );
          }
          return schema.nullable();
        }),
      providerFeelsSupported: yup
        .string()
        .defined()
        .oneOf(Object.values(ClientRoleProviderFeelsSupportedEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.provider_feels_supported_selected) {
            return schema.required(
              intl.formatMessage({ id: 'errors.contentConditions.providerFeelsSupported.required' })
            );
          }
          return schema.nullable();
        }),
      relationship: yup
        .string()
        .defined()
        .oneOf(Object.values(ClientRoleRelationshipEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.relationship_selected) {
            return schema.required(intl.formatMessage({ id: 'errors.contentConditions.relationship.required' }));
          }
          return schema.nullable();
        }),
      source: yup
        .string()
        .defined()
        .oneOf(Object.values(ContentConditionSourceEnum))
        .required(intl.formatMessage({ id: 'errors.contentConditions.source.required' })),
      stage: yup
        .string()
        .defined()
        .oneOf(Object.values(StageEnum))
        .when('kind', ([kind], schema) => {
          if (kind === ContentConditionKindEnum.stage_selected) {
            return schema.required(intl.formatMessage({ id: 'errors.contentConditions.stage.required' }));
          }
          return schema.nullable();
        })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const [createContentCondition, { client }] = useMutation<
    ContentConditionCreateMutation,
    ContentConditionCreateMutationVariables
  >(ContentConditionCreateDocument, {
    update: () => {
      // This sucks a little... we can update the article in the cache to include the new condition,
      // but there's also the query for the full condition list, and we cannot update that here.
      client.resetStore();
    }
  });

  const source = form.watch('source');
  const careProfileField = form.watch('careProfileField');
  const kind = form.watch('kind');

  const sourceOptions = Object.values(ContentConditionSourceEnum).map(option => ({
    label: intl.formatMessage({ id: `models.contentCondition.source.${option}` }),
    value: option
  }));

  const kindOptions = useMemo(
    () =>
      contentConditionKindsForSources([source]).map(option => ({
        label: intl.formatMessage({ id: `models.contentCondition.kind.${option}` }),
        value: option
      })),
    [source, intl]
  );

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    let useKind = kind;
    if (source === ContentConditionSourceEnum.ALWAYS) {
      useKind = ContentConditionKindEnum.always;
    } else if (source === ContentConditionSourceEnum.CARE_PROFILE) {
      useKind = ContentConditionKindEnum.care_profile_field_value;
    }

    if (useKind) {
      const contentData = {
        id: goalTemplate ? goalTemplate.id : article?.id,
        type: goalTemplate
          ? ConditionalContentTypeEnum.goal_template
          : article
            ? ConditionalContentTypeEnum.article
            : null
      };

      if (contentData.id && contentData.type) {
        return createContentCondition({
          variables: {
            ...form.getValues(),
            contentId: contentData.id,
            contentType: contentData.type,
            kind: useKind
          }
        }).then(response => {
          handleMutationResponse<FormData>({
            data: response.data?.contentConditionCreate,
            intl,
            onSuccess,
            setBaseErrors,
            setError: form.setError,
            txBase: 'errors.message'
          });
        });
      } else {
        onSuccess({ ...form.getValues(), kind: useKind });
      }
    }
  }, [article, createContentCondition, form, goalTemplate, intl, kind, onSuccess, source]);

  useEffect(() => {
    form.setValue('kind', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const providerEmotionalStateOptions = useMemo(() => {
    const options = [
      ClientRoleProviderEmotionalStateEnum.very_negative,
      ClientRoleProviderEmotionalStateEnum.negative,
      ClientRoleProviderEmotionalStateEnum.neutral,
      ClientRoleProviderEmotionalStateEnum.positive,
      ClientRoleProviderEmotionalStateEnum.very_positive
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.careProfile.providerEmotionalStateOptions.${option}`
      }),
      value: option
    }));
  }, [intl]);

  const providerFeelsSupportedOptions = useMemo(() => {
    const options = [
      ClientRoleProviderFeelsSupportedEnum.yes,
      ClientRoleProviderFeelsSupportedEnum.no,
      ClientRoleProviderFeelsSupportedEnum.its_complicated
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.careProfile.providerFeelsSupportedOptions.${option}`
      }),
      value: option
    }));
  }, [intl]);

  const relationshipOptions = optionsForRelationship(intl, 'models.clientRole.relationship');
  const stageOptions = optionsForStage(intl);

  const careProfileFieldOptions = optionsForCareProfileActionableField(intl);
  const careProfileValueOptions = optionsForCareProfileActionableFieldValue(careProfileField, intl);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.source}
          label={intl.formatMessage({ id: 'forms.contentConditions.source' })}
          name="source"
          options={sourceOptions}
        />
      </div>
      <div className={source === ContentConditionSourceEnum.CARE_PROFILE ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.careProfileField}
          label={intl.formatMessage({ id: 'forms.contentConditions.question' })}
          name="careProfileField"
          options={careProfileFieldOptions}
        />
        <div className={careProfileValueOptions ? undefined : 'ion-hide'}>
          <Select<FormData>
            control={form.control}
            error={form.formState.errors.careProfileValue}
            label={intl.formatMessage({ id: 'forms.contentConditions.answer' })}
            name="careProfileValue"
            options={careProfileValueOptions ?? []}
          />
        </div>
      </div>
      <div
        className={
          source && source !== ContentConditionSourceEnum.ALWAYS && source !== ContentConditionSourceEnum.CARE_PROFILE
            ? undefined
            : 'ion-hide'
        }
      >
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.kind}
          label={intl.formatMessage({ id: 'forms.contentConditions.criteria' })}
          name="kind"
          options={kindOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.stage_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.stage}
          label={intl.formatMessage({ id: 'forms.contentConditions.stage' })}
          name="stage"
          options={stageOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.provider_emotional_state_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.providerEmotionalState}
          label={intl.formatMessage({ id: 'forms.contentConditions.providerEmotionalState' })}
          name="providerEmotionalState"
          options={providerEmotionalStateOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.provider_feels_supported_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.providerFeelsSupported}
          label={intl.formatMessage({ id: 'forms.contentConditions.providerFeelsSupported' })}
          name="providerFeelsSupported"
          options={providerFeelsSupportedOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.relationship_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.relationship}
          label={intl.formatMessage({ id: 'forms.contentConditions.relationship' })}
          name="relationship"
          options={relationshipOptions}
        />
      </div>

      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default ContentConditionForm;
